import { BaseFirestore } from "./Firestore";
import { Timestamp } from "firebase/firestore";

export const CustomerUpdateStatus = {
  CREATED: "created",
  SCHEDULED: "scheduled",
  PENDING: "pending",
  SNOOZED: "snoozed",
  COMPLETED: "completed",
  SKIPPED: "skipped",
  CANCELLED: "cancelled",
} as const;

export type CustomerUpdateStatus =
  (typeof CustomerUpdateStatus)[keyof typeof CustomerUpdateStatus];

export const CustomerUpdateStatusLabels: Record<CustomerUpdateStatus, string> =
  {
    [CustomerUpdateStatus.CREATED]: "Not Scheduled",
    [CustomerUpdateStatus.SCHEDULED]: "Scheduled",
    [CustomerUpdateStatus.PENDING]: "Update Needed",
    [CustomerUpdateStatus.SNOOZED]: "Snoozed",
    [CustomerUpdateStatus.SKIPPED]: "Skipped",
    [CustomerUpdateStatus.CANCELLED]: "Final Update",
    [CustomerUpdateStatus.COMPLETED]: "Completed",
  };

export const CustomerUpdateTransition = {
  TO_CREATED: "to_created",
  CREATED_TO_SCHEDULED: "created_to_scheduled",
  SCHEDULED_TO_PENDING: "scheduled_to_pending",
  CREATED_TO_COMPLETED: "created_to_completed",
  PENDING_TO_COMPLETED: "pending_to_completed",
  PENDING_TO_SNOOZED: "pending_to_snoozed",
  PENDING_TO_SKIPPED: "pending_to_skipped",
  PENDING_TO_CANCELLED: "pending_to_cancelled",
  SNOOZED_TO_PENDING: "snoozed_to_pending",
  TO_DELETED: "to_deleted",
} as const;

export type CustomerUpdateTransition =
  (typeof CustomerUpdateTransition)[keyof typeof CustomerUpdateTransition];

export const CustomerUpdateTransitionStatuses: Record<
  CustomerUpdateTransition,
  { from: CustomerUpdateStatus; to: CustomerUpdateStatus }
> = {
  [CustomerUpdateTransition.TO_CREATED]: {
    from: CustomerUpdateStatus.CREATED,
    to: CustomerUpdateStatus.CREATED,
  },
  [CustomerUpdateTransition.CREATED_TO_SCHEDULED]: {
    from: CustomerUpdateStatus.CREATED,
    to: CustomerUpdateStatus.SCHEDULED,
  },
  [CustomerUpdateTransition.CREATED_TO_COMPLETED]: {
    from: CustomerUpdateStatus.CREATED,
    to: CustomerUpdateStatus.COMPLETED,
  },
  [CustomerUpdateTransition.SCHEDULED_TO_PENDING]: {
    from: CustomerUpdateStatus.SCHEDULED,
    to: CustomerUpdateStatus.PENDING,
  },
  [CustomerUpdateTransition.PENDING_TO_CANCELLED]: {
    from: CustomerUpdateStatus.PENDING,
    to: CustomerUpdateStatus.CANCELLED,
  },
  [CustomerUpdateTransition.PENDING_TO_COMPLETED]: {
    from: CustomerUpdateStatus.PENDING,
    to: CustomerUpdateStatus.COMPLETED,
  },
  [CustomerUpdateTransition.PENDING_TO_SNOOZED]: {
    from: CustomerUpdateStatus.PENDING,
    to: CustomerUpdateStatus.SNOOZED,
  },
  [CustomerUpdateTransition.PENDING_TO_SKIPPED]: {
    from: CustomerUpdateStatus.PENDING,
    to: CustomerUpdateStatus.SKIPPED,
  },
  [CustomerUpdateTransition.SNOOZED_TO_PENDING]: {
    from: CustomerUpdateStatus.SNOOZED,
    to: CustomerUpdateStatus.PENDING,
  },
  [CustomerUpdateTransition.TO_DELETED]: {
    from: CustomerUpdateStatus.CREATED,
    to: CustomerUpdateStatus.CREATED, // This is a placeholder; actual deletion logic may vary
  },
};

export const CustomerUpdateMedium = {
  PHONE: "phone",
  TEXT: "text",
  EMAIL: "email",
  INPERSON: "inperson",
  OTHER: "other",
  UNKNOWN: "unknown",
} as const;

export type CustomerUpdateMedium =
  (typeof CustomerUpdateMedium)[keyof typeof CustomerUpdateMedium];

export const CustomerUpdateMediumLabels: Record<CustomerUpdateMedium, string> =
  {
    [CustomerUpdateMedium.PHONE]: "Phone",
    [CustomerUpdateMedium.TEXT]: "Text",
    [CustomerUpdateMedium.EMAIL]: "Email",
    [CustomerUpdateMedium.INPERSON]: "In Person",
    [CustomerUpdateMedium.OTHER]: "Other",
    [CustomerUpdateMedium.UNKNOWN]: "Unknown",
  };

export type CustomerUpdate = BaseFirestore & {
  id: string;
  desiredUpdateTime?: Date | Timestamp; // The time the customer update is desired to be completed
  scheduledUpdateTime?: Date | Timestamp; // Actual time that the update was scheduled (based on the desired time and other scheduled updates)
  actualUpdateTime?: Date | Timestamp; // The actual time the update was sent
  updateDate?: string;
  updateMedium?: CustomerUpdateMedium[];
  shopId: string;
  cardId: string;
  complete: boolean;
  completedOnTime?: boolean;
  completedOnTimeBuffer: number; // in minutes
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedBy: string;
  updatedAt: Date | Timestamp;
  scheduledAt?: Date | Timestamp; // The time the update was scheduled
  scheduledBy?: string; // The user who scheduled the update
  pendingAt?: Date | Timestamp; // The time the update was marked as pending
  pendingBy?: string; // The user who marked the update as pending
  completedAt?: Date | Timestamp; // The time the update was completed
  completedBy?: string; // The user who completed the update
  comments?: string;
  skipped?: boolean;
  skippedAt?: Date | Timestamp;
  skippedBy?: string;
  skippedReason?: string;
  cancelledAt?: Date | Timestamp; // The time the update was cancelled
  cancelledBy?: string; // The user who cancelled the update
  cancelledReason?: string; // The reason for cancelling the update
  userId?: string | null; // The user who is responsible for this update
  status: CustomerUpdateStatus;
  snoozes?: Snooze[]; // Array of snoozes for this update
  snoozeCount?: number; // Number of times the update has been snoozed
  // Card info for display and sorting/filtering purposes
  cardNumber?: string | null; // The card number associated with the update
  cardSource?: string | null; // The card source (e.g., "manual", "import", "api")
  customerDisplayName?: string | null; // The card customer
  customerId?: string | null; // The card customer ID
  vehicle?: string | null; // The card vehicle
  vehicleId?: string | null; // The card vehicle ID
  lastCustomerUpdate?: CustomerUpdate | null;
  completedInContainer?: string | null;
};

export type Snooze = {
  snoozedAt: Date | Timestamp; // The time the update was snoozed
  snoozedBy: string; // The user who snoozed the update
  originalDesiredUpdateTime: Date | Timestamp; // The original desired update time before snoozing
  originalScheduledUpdateTime?: Date | Timestamp; // The original scheduled update time before snoozing
  originalActualUpdateTime?: Date | Timestamp; // The original actual update time before snoozing
};

export type CreateCustomerUpdateDto = Omit<
  CustomerUpdate,
  "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
>;
export const MetricsPeriod = {
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  CUSTOM: "custom",
} as const;

export type MetricsPeriod = (typeof MetricsPeriod)[keyof typeof MetricsPeriod];

/**
 * Aggregate totals across all customer updates in a given period.
 * - Percent fields are 0..100 (not a fraction).
 * - averageTimeToCompletionMs is the mean duration in milliseconds between
 *   scheduledUpdateTime and completedAt for completed updates in the period.
 */
export type CustomerUpdateMetricsTotals = {
  /** Total number of customer updates considered in the period. */
  totalUpdates: number;
  /** Number of those updates that were completed (completedAt exists). */
  completedCount: number;
  /** Number of completed updates that were completed on time. */
  onTimeCompletedCount: number;
  /** Percentage of completed updates completed on time (0..100). */
  percentCompletedOnTime: number;
  /** Average number of snoozes per update (across all updates in scope). */
  averageSnoozesPerUpdate: number;
  /**
   * Average time to completion in milliseconds, comparing completedAt to
   * scheduledUpdateTime. Null if there are no completed updates in the period.
   */
  averageTimeToCompletionMs: number | null;
};

/**
 * Per-user breakdown keyed by the user who completed the update (completedBy).
 * - userId should match the completedBy value on CustomerUpdate.
 * - Percent fields are 0..100 (not a fraction).
 */
export type CustomerUpdateMetricsByUser = {
  /** The userId that matches CustomerUpdate.completedBy. */
  userId: string;
  /** Number of updates this user completed in the period. */
  completedCount: number;
  /** Number of those completed on time. */
  onTimeCompletedCount: number;
  /** Percentage of this user's completed updates completed on time (0..100). */
  percentCompletedOnTime: number;
  /** Average number of snoozes per update among this user's completed updates. */
  averageSnoozesPerUpdate: number;
  /** Average time to completion in milliseconds for this user's completed updates. */
  averageTimeToCompletionMs: number | null;
};

/**
 * Firestore document shape for scheduled, periodic Customer Update metrics.
 * Intended to be created by a scheduled Cloud Function (e.g., weekly/monthly).
 */
export type CustomerUpdateMetrics = BaseFirestore & {
  /** Firestore document id. */
  id: string;
  /** Shop scope for which metrics are computed. */
  shopId: string;
  /** The type of period for this metrics document (weekly, monthly, or custom). */
  period: MetricsPeriod;
  /** Inclusive start of period. */
  periodStart: Date | Timestamp;
  /** Exclusive end of period. */
  periodEnd: Date | Timestamp;
  /** When the metrics document was generated. */
  generatedAt: Date | Timestamp;
  /**
   * Overall totals across the shop for the given period.
   */
  totals: CustomerUpdateMetricsTotals;
  /**
   * Breakdown by the user who completed the update. The key is the userId
   * (same value as CustomerUpdate.completedBy). If a userId is unknown, you
   * may use "unknown" or another sentinel key.
   */
  byUser: Record<string, CustomerUpdateMetricsByUser>;
};
